import { useEffect, useState, useRef } from "react";
import RechargePixView from "./view";
import { getFormattedCart, getIsValidString } from "@/utils/functions";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { paymentPix, setCustomer } from "@/redux/ducks/customer";
import moment from "moment";
import { ReducersTypes } from "@/redux/reducers/Models";
import { usePixPaymentStatus } from "@/hooks/usePixPaymentStatus";
import { usePixTimer } from "@/hooks/usePixTimer";
import { getCheckoutSuccessPath } from "@/pages/checkout/getCheckoutSuccessPath";
import { trackPurchase } from "@/analytics/ecommerce";

interface UI {
  isLoading: boolean;
  error: boolean;
  isVisibleModal: boolean;
  showPaymentErrorModal: boolean;
}

interface PIX {
  code: string;
  expiration: string;
  image: null | {
    image: string;
    type: string;
    width: number;
  };
}

export default function RechargePix() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const customer = useSelector((state: ReducersTypes) => state.customerReducer);

  const [ui, setUi] = useState<UI>({
    isLoading: false,
    error: false,
    isVisibleModal: false,
    showPaymentErrorModal: false,
  });
  const [pixInformations, setPixInformations] = useState<PIX>({
    code: "",
    expiration: "",
    image: null,
  });
  const [transactionId, setTransactionId] = useState<string>("");
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const { formattedTime, startTimer } = usePixTimer({
    duration: 600,
    onExpire: () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      goHome();
    },
  });

  const { total, filteredItems } = getFormattedCart({
    debts: customer?.debts!,
    cart: customer?.cart?.filter((item) => item.checked) ?? [],
  });

  const getSelectedPlates = () => {
    if (!filteredItems || filteredItems.length === 0) return "";
    const plates = filteredItems.map((item) => item.plate);
    const uniquePlates = plates.filter(
      (plate, index) => plates.indexOf(plate) === index,
    );
    return uniquePlates.join(", ");
  };

  const goBack = () => navigate("/payment");
  const goHome = () => navigate("/home");
  const goSuccess = () => {
    if (customer?.refreshToken) {
      navigate("/payment/finished");
    } else {
      navigate(getCheckoutSuccessPath());
    }
  };

  const toggleModal = () =>
    setUi((prev) => ({ ...prev, isVisibleModal: !prev.isVisibleModal }));
  const togglePaymentErrorModal = () =>
    setUi((prev) => ({ ...prev, showPaymentErrorModal: false }));

  const copyCode = async () => {
    if (!getIsValidString(pixInformations.code) || ui.isLoading) return;
    try {
      await navigator.clipboard.writeText(pixInformations.code);
      setUi((prev) => ({ ...prev, isVisibleModal: true }));
    } catch (err) {}
  };

  const share = async () => {
    if (!getIsValidString(pixInformations.code) || ui.isLoading) return;
    try {
      const shareData = {
        title: "Pix",
        text: pixInformations.code,
      };
      await navigator.share(shareData);
    } catch (err) {}
  };

  const handlePix = (
    success: boolean,
    data?: {
      code?: string;
      image?: string;
      imageType?: string;
      imageWidth?: number;
      codeExpiration?: string;
      idTransaction?: string;
      sqPagamento?: string;
      errorCode?: string;
    },
  ) => {
    setUi((prev) => ({ ...prev, isLoading: false }));

    // Handle specific error code 422
    if (!success && data?.errorCode === "422") {
      setUi((prev) => ({ ...prev, showPaymentErrorModal: true }));
      return;
    }

    if (!success) {
      setUi((prev) => ({ ...prev, error: true }));
      return;
    }

    setUi((prev) => ({ ...prev, error: false }));
    const date = new Date(data?.codeExpiration ?? "");
    const formattedDate = moment(date).format("DD/MM/YYYY [às] HH:mm:ss");
    setPixInformations((prev) => ({
      ...prev,
      expiration: formattedDate,
      image: data?.image
        ? {
            image: data.image!,
            type: data.imageType!,
            width: data.imageWidth!,
          }
        : null,
      code: data?.code!,
    }));
    if (data?.idTransaction) {
      setTransactionId(data.idTransaction);
    }
    if (data?.sqPagamento) {
      dispatch(setCustomer({ sqPagamento: data.sqPagamento }));
    }
  };

  const payment = () => {
    if (ui.isLoading) return;
    setUi((prev) => ({ ...prev, isLoading: true }));
    dispatch(
      paymentPix({
        callback: handlePix,
      }),
    );
  };

  useEffect(() => {
    if (transactionId) {
      startTimer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionId]);

  const { data: paymentStatus, refetch } = usePixPaymentStatus(
    transactionId,
    customer?.requestId as string,
    !!transactionId,
  );
  useEffect(() => {
    if (!transactionId) {
      return;
    }
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(() => {
      if (refetch) {
        refetch().catch((err) => {
          console.error("Erro no polling:", err);
        });
      }
    }, 10000);
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [transactionId, refetch]);

  useEffect(() => {
    if (paymentStatus?.paymentStatus === 1) {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (paymentStatus.sqPagamento) {
        dispatch(setCustomer({ sqPagamento: paymentStatus.sqPagamento }));
      }
      trackPurchase({
        flowType: "pagamento_cadastrado",
        paymentType: "pix",
        value: total,
        transactionId,
      });
      goSuccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStatus]);

  useEffect(() => {
    payment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <RechargePixView
      isLoading={ui.isLoading}
      goBack={goBack}
      goHome={goHome}
      payment={payment}
      pixCode={pixInformations.code}
      pixImage={pixInformations.image}
      copyCode={copyCode}
      toggleModal={toggleModal}
      isVisibleModal={ui.isVisibleModal}
      share={share}
      error={ui.error}
      codeExpiration={pixInformations.expiration}
      total={total}
      plates={getSelectedPlates()}
      timer={formattedTime}
      showPaymentErrorModal={ui.showPaymentErrorModal}
      togglePaymentErrorModal={togglePaymentErrorModal}
    />
  );
}
